import { apiClient } from '../client';

/**
 * Accounts asking to be restored after deletion.
 *
 * Shaped to match deletionRequestsService, because the review screens are the
 * same job seen from the other side.
 */

export interface ReactivationRequestUserSnapshot {
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  school_id?: {
    _id: string;
    school_name: string;
  };
  campus_id?: {
    _id: string;
    campus_name: string;
  };
}

export interface ReactivationRequest {
  _id: string;
  user_id: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  } | string;
  email: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  user_snapshot: ReactivationRequestUserSnapshot;
  /** The deadline. Past it the request can no longer be approved. */
  recoverable_until: string;
  reviewed_by?: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  reviewed_at?: string;
  resolution_notes?: string;
  restored_at?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReactivationRequestStats {
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
  total: number;
}

export interface ReactivationRequestFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  school_id?: string;
  campus_id?: string;
}

interface ReactivationRequestsResponse {
  success: boolean;
  data: ReactivationRequest[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export const reactivationRequestsService = {
  getReactivationRequests: async (
    filters?: ReactivationRequestFilters
  ): Promise<{
    requests: ReactivationRequest[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const params = new URLSearchParams();

    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.school_id) params.append('school_id', filters.school_id);
    if (filters?.campus_id) params.append('campus_id', filters.campus_id);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<ReactivationRequestsResponse>(
      `/api/admin/reactivation-requests?${params.toString()}`
    );

    return {
      requests: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
    };
  },

  getStats: async (filters?: {
    school_id?: string;
    campus_id?: string;
  }): Promise<ReactivationRequestStats> => {
    const params = new URLSearchParams();

    if (filters?.school_id) params.append('school_id', filters.school_id);
    if (filters?.campus_id) params.append('campus_id', filters.campus_id);

    const response = await apiClient.get<{ success: boolean; data: ReactivationRequestStats }>(
      `/api/admin/reactivation-requests/stats?${params.toString()}`
    );

    return response.data.data;
  },

  getDetails: async (requestId: string): Promise<ReactivationRequest> => {
    const response = await apiClient.get(`/api/admin/reactivation-requests/${requestId}`);
    return response.data.data;
  },

  // Approving restores the account in the same call — there is no separate
  // "complete" step, unlike deletion.
  approve: async (requestId: string, resolutionNotes?: string) => {
    const response = await apiClient.post(
      `/api/admin/reactivation-requests/${requestId}/approve`,
      { resolution_notes: resolutionNotes }
    );
    return response.data.data;
  },

  reject: async (requestId: string, resolutionNotes: string) => {
    const response = await apiClient.post(
      `/api/admin/reactivation-requests/${requestId}/reject`,
      { resolution_notes: resolutionNotes }
    );
    return response.data.data;
  },
};

export { reactivationRequestsService as reactivationRequestsAPI };
